import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Label,
  PrimaryButton,
  Stack,
  Text,
  TextField,
  mergeStyleSets,
} from '@fluentui/react';
import React from 'react';
import { isMobile } from 'react-device-detect';
import Header from '../../shared/callback/Header';
import HelpButton from '../../shared/help/HelpButton';
import ValidatedTextField from '../../shared/ValidatedTextField';
import StackTokens from '../../shared/StackTokens';
import Theme from '../../shared/Theme';
import TextFieldBuffered from '../../shared/TextFieldBuffered';
import {
  dataSourceIconAcceptAttribute,
  dataSourceIconSrc,
  DATA_SOURCE_ICON_MAX_STORED_PX,
  DATA_SOURCE_ICON_MENU_PX,
  processDataSourceIconFile,
} from '../../shared/util/DataSourceIcon';

const componentStyles = mergeStyleSets({
  callbackHeaders: {
    marginTop: '0 !important',
    selectors: {
      label: {
        padding: 0,
      },
    },
  },

});

export const defaultObservationSource = {
  name: '',
  url: '',
  ttl: 60,
  modelViewEnabled: true,
  appViewEnabled: true,
  ignoreFailures: true,
  headers: {},
  icon: '',
};

export const createObservationSourceId = () => crypto.randomUUID();

export const buildObservationSource = (source = {}) => {
  const sourceWithDefaults = {
    ...defaultObservationSource,
    ...source,
  };
  if (!sourceWithDefaults.id) {
    sourceWithDefaults.id = createObservationSourceId();
  }
  return sourceWithDefaults;
};

const trimHeaderPart = (value) => `${value ?? ''}`.trim();

export const emptyFieldErrors = () => ({
  name: '',
  url: '',
  ttl: '',
  icon: '',
  newHeaderName: '',
  newHeaderValue: '',
  headers: {},
});

export const validateHeaderPair = (name, value, { allowPartial = false } = {}) => {
  const trimmedName = trimHeaderPart(name);
  const trimmedValue = trimHeaderPart(value);
  let nameError = '';
  let valueError = '';

  if (allowPartial) {
    if (trimmedName || trimmedValue) {
      if (!trimmedName) {
        nameError = 'Header name is required';
      }
      if (!trimmedValue) {
        valueError = 'Header value is required';
      }
    }
    return {
      nameError, valueError, trimmedName, trimmedValue,
    };
  }

  if (!trimmedName) {
    nameError = 'Header name is required';
  }
  if (!trimmedValue) {
    valueError = 'Header value is required';
  }
  return {
    nameError, valueError, trimmedName, trimmedValue,
  };
};

export const hasFieldErrors = (fieldErrors) => (
  !!(fieldErrors.name || fieldErrors.url || fieldErrors.ttl || fieldErrors.icon
    || fieldErrors.newHeaderName || fieldErrors.newHeaderValue
    || Object.values(fieldErrors.headers).some((headerErrors) => headerErrors.name || headerErrors.value))
);

export const validateObservationSourceFields = (source, pendingHeader = {}) => {
  const fieldErrors = emptyFieldErrors();
  const name = `${source.name ?? ''}`.trim();
  const url = `${source.url ?? ''}`.trim();

  if (!name) {
    fieldErrors.name = 'Source name is required';
  }
  if (!url) {
    fieldErrors.url = 'Source URL is required';
  }

  const ttl = Number(source.ttl);
  if (Number.isNaN(ttl)) {
    fieldErrors.ttl = 'TTL must be a number';
  } else if (ttl < 0) {
    fieldErrors.ttl = 'TTL must be zero or greater';
  }

  Object.entries(source.headers || {}).forEach(([headerName, headerValue]) => {
    const { nameError, valueError } = validateHeaderPair(headerName, headerValue);
    if (nameError || valueError) {
      fieldErrors.headers[headerName] = { name: nameError, value: valueError };
    }
  });

  const pending = validateHeaderPair(pendingHeader.name, pendingHeader.value, { allowPartial: true });
  fieldErrors.newHeaderName = pending.nameError;
  fieldErrors.newHeaderValue = pending.valueError;

  return fieldErrors;
};

const normalizeObservationSourceDraft = (source) => ({
  ...source,
  name: `${source.name ?? ''}`.trim(),
  url: `${source.url ?? ''}`.trim(),
});

export default function ObservationSourceDialog({
  isOpen,
  title,
  confirmText,
  source,
  onDismiss,
  onConfirm,
}) {
  const [draft, setDraft] = React.useState(() => buildObservationSource(source));
  const [newHeaderName, setNewHeaderName] = React.useState('');
  const [newHeaderValue, setNewHeaderValue] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState(emptyFieldErrors);
  const [iconBusy, setIconBusy] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const iconFileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen) {
      setDraft(buildObservationSource(source));
      setNewHeaderName('');
      setNewHeaderValue('');
      setFieldErrors(emptyFieldErrors());
      setIsSaving(false);
    }
  }, [isOpen, source]);

  if (!isOpen) {
    return null;
  }

  const activeHeaders = draft.headers || {};

  const updateField = (fieldName, fieldValue) => {
    setDraft((current) => ({
      ...defaultObservationSource,
      ...current,
      [fieldName]: fieldValue,
    }));
  };

  const onIconFileSelected = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    setIconBusy(true);
    const result = await processDataSourceIconFile(file);
    setIconBusy(false);
    if (result.error) {
      setFieldErrors((current) => ({ ...current, icon: result.error }));
      return;
    }
    setFieldErrors((current) => ({ ...current, icon: '' }));
    updateField('icon', result.dataUrl);
  };

  const addHeader = () => {
    const {
      nameError, valueError, trimmedName, trimmedValue,
    } = validateHeaderPair(newHeaderName, newHeaderValue);
    if (nameError || valueError) {
      setFieldErrors((current) => ({
        ...current,
        newHeaderName: nameError,
        newHeaderValue: valueError,
      }));
      return;
    }
    setFieldErrors((current) => ({
      ...current,
      newHeaderName: '',
      newHeaderValue: '',
    }));
    updateField('headers', {
      ...activeHeaders,
      [trimmedName]: trimmedValue,
    });
    setNewHeaderName('');
    setNewHeaderValue('');
  };

  const updateHeader = (previousName, name, value) => {
    const {
      nameError, valueError, trimmedName, trimmedValue,
    } = validateHeaderPair(name, value);
    if (nameError || valueError) {
      setFieldErrors((current) => ({
        ...current,
        headers: {
          ...current.headers,
          [previousName]: { name: nameError, value: valueError },
        },
      }));
      return;
    }
    setFieldErrors((current) => {
      const nextHeaders = { ...current.headers };
      delete nextHeaders[previousName];
      return { ...current, headers: nextHeaders };
    });
    const updatedHeaders = { ...activeHeaders };
    delete updatedHeaders[previousName];
    updatedHeaders[trimmedName] = trimmedValue;
    updateField('headers', updatedHeaders);
  };

  const handleConfirm = async () => {
    const nextFieldErrors = validateObservationSourceFields(draft, {
      name: newHeaderName,
      value: newHeaderValue,
    });
    setFieldErrors(nextFieldErrors);
    if (hasFieldErrors(nextFieldErrors)) {
      return;
    }

    setIsSaving(true);
    await onConfirm(buildObservationSource(normalizeObservationSourceDraft(draft)));
    setIsSaving(false);
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={() => {
        if (!isSaving) {
          onDismiss();
        }
      }}
      dialogContentProps={{
        type: DialogType.normal,
        title,
        closeButtonAriaLabel: 'Cancel',
      }}
      modalProps={{
        isBlocking: true,
      }}
      minWidth={isMobile ? undefined : '520px'}
    >
      <Stack vertical tokens={StackTokens.verticalSpacing}>
        <TextField
          label="Source name"
          value={draft.name || ''}
          errorMessage={fieldErrors.name}
          onChange={(ev, value) => {
            updateField('name', value ?? '');
            setFieldErrors((current) => ({ ...current, name: '' }));
          }}
        />

        <Stack vertical tokens={StackTokens.spacing5}>
          <Label>Source icon (optional)</Label>
          <Text block styles={{ root: { fontSize: 12, color: Theme.Colors.DisabledText } }}>
            JPEG, JPG, GIF, PNG, SVG, or WebP.
          </Text>
          <Text block styles={{ root: { fontSize: 12, color: Theme.Colors.DisabledText } }}>
            Images are resized to
            {' '}
            {DATA_SOURCE_ICON_MAX_STORED_PX}
            ×
            {DATA_SOURCE_ICON_MAX_STORED_PX}
            {' '}
            px for storage and to
            {' '}
            {DATA_SOURCE_ICON_MENU_PX}
            ×
            {DATA_SOURCE_ICON_MENU_PX}
            {' '}
            px for display.
          </Text>
          <input
            type="file"
            accept={dataSourceIconAcceptAttribute()}
            style={{ display: 'none' }}
            ref={iconFileInputRef}
            onChange={onIconFileSelected}
          />
          {(draft.icon && `${draft.icon}`.trim()) ? (
            <Stack horizontal tokens={StackTokens.spacingWide} verticalAlign="end" wrap>
              <Stack vertical tokens={StackTokens.spacing5}>
                <img
                  alt=""
                  src={dataSourceIconSrc(draft.icon)}
                  width={DATA_SOURCE_ICON_MAX_STORED_PX}
                  height={DATA_SOURCE_ICON_MAX_STORED_PX}
                  style={{
                    display: 'block',
                    border: `1px solid ${Theme.Colors.LightGray}`,
                    objectFit: 'contain',
                  }}
                />
              </Stack>
              <Stack vertical tokens={StackTokens.spacing5}>
                <Text styles={{ root: { fontSize: 12, color: Theme.Colors.DisabledText } }}>
                  {`${DATA_SOURCE_ICON_MENU_PX}×${DATA_SOURCE_ICON_MENU_PX}`}
                </Text>
                <img
                  alt=""
                  src={dataSourceIconSrc(draft.icon)}
                  width={DATA_SOURCE_ICON_MENU_PX}
                  height={DATA_SOURCE_ICON_MENU_PX}
                  style={{
                    display: 'block',
                    border: `1px solid ${Theme.Colors.LightGray}`,
                    objectFit: 'contain',
                    imageRendering: 'pixelated',
                  }}
                />
              </Stack>
            </Stack>
          ) : null}
          <Stack horizontal tokens={StackTokens.spacing5}>
            <DefaultButton
              text="Choose icon file…"
              disabled={iconBusy}
              className={Theme.styles.outlineControlButton}
              onClick={() => {
                setFieldErrors((current) => ({ ...current, icon: '' }));
                if (iconFileInputRef.current) {
                  iconFileInputRef.current.click();
                }
              }}
            />
            <DefaultButton
              text="Remove icon"
              disabled={iconBusy || !(draft.icon && `${draft.icon}`.trim())}
              className={Theme.styles.outlineControlButton}
              onClick={() => {
                updateField('icon', '');
                setFieldErrors((current) => ({ ...current, icon: '' }));
              }}
            />
          </Stack>
          {fieldErrors.icon ? (
            <Text role="alert" styles={{ root: { fontSize: 12, color: '#a4262c' } }}>
              {fieldErrors.icon}
            </Text>
          ) : null}
        </Stack>

        <TextFieldBuffered
          label="Full source URL"
          actualValue={draft.url || ''}
          fieldError={fieldErrors.url}
          onFieldErrorClear={() => setFieldErrors((current) => ({ ...current, url: '' }))}
          validateValue={(value) => value}
          applyValue={(value) => updateField('url', value)}
        />

        <ValidatedTextField
          label="TTL (How many seconds can the source be cached for)"
          initialValue={(draft.ttl ?? 600).toString()}
          value={(draft.ttl ?? 600).toString()}
          fieldError={fieldErrors.ttl}
          onFieldErrorClear={() => setFieldErrors((current) => ({ ...current, ttl: '' }))}
          setValue={(value) => {
            const parsedValue = Number(value);
            if (Number.isNaN(parsedValue)) {
              return { success: false, error: 'Value must be a number' };
            }

            if (parsedValue < 0) {
              return { success: false, error: 'Value must be zero or greater' };
            }

            updateField('ttl', parsedValue);
            setFieldErrors((current) => ({ ...current, ttl: '' }));
            return { success: true, value: parsedValue };
          }}
        />

        <Checkbox
          label={(
            <Stack horizontal tokens={StackTokens.spacing5}>
              <Text>Enable in Form View</Text>
              <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Help">
                <div style={isMobile ? {} : { maxWidth: '45vw' }}>
                  {[
                    'If checked, this source will be used for calculations made from Form View.',
                    'Non-optional sources will be applied automatically during calculation.',
                    'If the same variable is present in multiple sources, the data from the last source in the list will be used.',
                  ].map((p) => <Text block key={p}>{p}</Text>)}
                </div>
              </HelpButton>
            </Stack>
          )}
          checked={draft.appViewEnabled ?? true}
          onChange={(ev, checked) => updateField('appViewEnabled', !!checked)}
        />

        <Checkbox
          label={(
            <Stack horizontal tokens={StackTokens.spacing5}>
              <Text>Enable in Model View</Text>
              <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Help">
                <div style={isMobile ? {} : { maxWidth: '45vw' }}>
                  {[
                    'If checked, this source will be used for calculations made from Model View.',
                    'The user will be able to load this data with a button in the "Data" menu under every individual existing case.',
                    'Once loaded, the data will be applied to the case on top of currently entered data.',
                    'If the same variable is present in multiple sources, the data from the last source in the list will be used.',
                  ].map((p) => <Text block key={p}>{p}</Text>)}
                </div>
              </HelpButton>
            </Stack>
          )}
          checked={draft.modelViewEnabled ?? true}
          onChange={(ev, checked) => updateField('modelViewEnabled', !!checked)}
        />

        <Checkbox
          checked={draft.ignoreFailures ?? true}
          onChange={(ev, checked) => updateField('ignoreFailures', !!checked)}
          label={(
            <Stack horizontal tokens={StackTokens.spacing5}>
              <Text>Ignore errors</Text>
              <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Help">
                <div style={isMobile ? {} : { maxWidth: '45vw' }}>
                  <p>If any case data entries within a source are not formatted correctly, ignore them and continue applying other data from this source.</p>
                </div>
              </HelpButton>
            </Stack>
          )}
        />

        <Checkbox
          checked={draft.optional ?? false}
          onChange={(ev, checked) => updateField('optional', !!checked)}
          label={(
            <Stack horizontal tokens={StackTokens.spacing5}>
              <Text>Optional</Text>
              <HelpButton styles={{ root: { marginLeft: '5px' } }} title="Help">
                <div style={isMobile ? {} : { maxWidth: '45vw' }}>
                  {[
                    'If checked, this source will be presented as a choice to the user in the UI.',
                    'In Form View, this can be found in the hamburger menu under "Case data sources".',
                    'In Model View, this can be found in the "Data" menu under every individual existing case.',
                    'When clicked, first all non-optional sources are collated into a single set of data, then the optional sources are merged into that set.',
                    'If the same variable is present in multiple sources, the data from the last source in the list will be used.',
                    'The final collated data is then applied to the case on top of currently entered data.',
                    'For Form View, make sure that the form is configured to allow entries for the variables in the optional sources for them to be visible to the user.',
                  ].map((p) => <Text block key={p}>{p}</Text>)}
                </div>
              </HelpButton>
            </Stack>
          )}
        />

        <Label>Custom headers</Label>
        <Header
          headerName={newHeaderName}
          headerValue={newHeaderValue}
          onHeaderNameChange={setNewHeaderName}
          onHeaderValueChange={setNewHeaderValue}
          nameError={fieldErrors.newHeaderName}
          valueError={fieldErrors.newHeaderValue}
          onNameErrorClear={() => setFieldErrors((current) => ({ ...current, newHeaderName: '' }))}
          onValueErrorClear={() => setFieldErrors((current) => ({ ...current, newHeaderValue: '' }))}
          add={addHeader}
          className={componentStyles.callbackHeaders}
          stretch
        />

        <Stack vertical tokens={StackTokens.spacing5}>
          {
            Object.entries(activeHeaders).map(([headerName, headerValue]) => (
              <Header
                key={headerName}
                headerName={headerName}
                headerValue={headerValue}
                nameError={fieldErrors.headers[headerName]?.name}
                valueError={fieldErrors.headers[headerName]?.value}
                onFieldErrorClear={() => setFieldErrors((current) => {
                  const nextHeaders = { ...current.headers };
                  delete nextHeaders[headerName];
                  return { ...current, headers: nextHeaders };
                })}
                apply={(hName, hValue) => updateHeader(headerName, hName, hValue)}
                remove={(hName) => {
                  const updatedHeaders = { ...activeHeaders };
                  delete updatedHeaders[hName];
                  updateField('headers', updatedHeaders);
                }}
                stretch
              />
            ))
          }
        </Stack>
      </Stack>

      <DialogFooter>
        <PrimaryButton
          text={confirmText}
          onClick={handleConfirm}
          disabled={isSaving}
          className={Theme.styles.outlineControlButton}
        />
        <DefaultButton
          text="Cancel"
          onClick={onDismiss}
          disabled={isSaving}
          className={Theme.styles.outlineControlButton}
        />
      </DialogFooter>
    </Dialog>
  );
}
